import { A11yModule } from '@angular/cdk/a11y';
import { ChangeDetectionStrategy, Component, OnDestroy, computed, input, output, signal } from '@angular/core';
import { ViewportOverlayDirective } from '@/app/common/directives/viewport-overlay/viewport-overlay.directive';

/** Which icon sits inside the spinner while the overlay is working. */
export type OverlayHeadIcon = 'scan' | 'send';

/** Stage cadence in ms (720ms default / 240ms reduced-motion). */
export const OVERLAY_STAGE_MS = 720;
export const OVERLAY_STAGE_MS_REDUCED = 240;

/**
 * The full-screen stage used by both the pre-flight check and the submitted state.
 *
 * It is purely presentational: it walks the stage list on a timer and renders whichever result the
 * parent hands it. Actions are projected via `[overlayActions]`, so the click handlers stay with
 * whoever owns the navigation.
 *
 * Mount it with `@if` — `ViewportOverlayDirective` re-parents the host to `<body>` and removes it on
 * destroy, so hiding it with `[hidden]` would strand a detached node.
 */
@Component({
  selector: 'sbs-screening-overlay',
  changeDetection: ChangeDetectionStrategy.OnPush,
  hostDirectives: [ViewportOverlayDirective],
  imports: [A11yModule],
  templateUrl: './screening-overlay.html',
  styleUrl: './screening-overlay.scss',
})
export class ScreeningOverlay implements OnDestroy {

  /** Labels of the stages that tick past while the overlay works. */
  readonly stages = input<readonly string[]>([]);
  readonly headIcon = input<OverlayHeadIcon>('scan');
  readonly headline = input<string>('');
  /** Sub-line built as `{{ subLead }} <b>{{ subject }}</b> {{ subTrail }}`. */
  readonly subLead = input<string>('');
  readonly subject = input<string>('');
  readonly subTrail = input<string>('');

  /** The parent flips this; `running` keeps the stage list spinning. */
  readonly result = input<'running' | 'pass' | 'fail'>('running');
  readonly resultTitle = input<string>('');
  readonly resultSub = input<string>('');
  readonly reasons = input<readonly string[]>([]);
  /** `check-check` is the submitted state's double tick; the check overlay uses a single one. */
  readonly passIcon = input<'check' | 'check-check'>('check');

  /** Emitted once, after the first full pass through the stage list. */
  readonly sequenceComplete = output<void>();

  readonly activeStage = signal<number>(0);

  readonly isRunning = computed(() => this.result() === 'running');

  private readonly timer: ReturnType<typeof setInterval> | undefined;
  private readonly cycleTimer: ReturnType<typeof setTimeout> | undefined;

  constructor() {
    const reduce =
      typeof window !== 'undefined' &&
      typeof window.matchMedia === 'function' &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    const stageMs = reduce ? OVERLAY_STAGE_MS_REDUCED : OVERLAY_STAGE_MS;

    // Loop rather than stop at the end: the submitted overlay waits on an HTTP response, which can
    // outlast the stage list.
    this.timer = setInterval(() => {
      this.activeStage.update((index) => (index + 1) % (this.stages().length + 1));
    }, stageMs);

    this.cycleTimer = setTimeout(() => this.sequenceComplete.emit(), stageMs * this.stages().length);
  }

  ngOnDestroy(): void {
    if (this.timer !== undefined) {
      clearInterval(this.timer);
    }
    if (this.cycleTimer !== undefined) {
      clearTimeout(this.cycleTimer);
    }
  }
}
